import React, {Component} from 'react';
import {action, observable, computed} from 'mobx';
import {inject, observer} from 'mobx-react';
import {
  Button, FormInput, FormSelect, FormCheckbox, FormTextarea,
  Modal, ModalBody, ModalHeader, ModalFooter
} from 'shards-react';
import FeatherIcon from 'feather-icons-react';
import {toast} from 'react-toastify';
import moment from 'moment';
import PageTitle from '../../../components/common/PageTitle';
import SwiperCore, {Autoplay, Navigation, Pagination, EffectFade} from 'swiper';
import {Swiper, SwiperSlide} from 'swiper/react';
import 'swiper/swiper-bundle.min.css';
import './style.scss';
import './banner.scss';

SwiperCore.use([Autoplay, Navigation, Pagination, EffectFade]);

const POSITIONS = [
  {value: 'main_top',     label: '메인 상단'},
  {value: 'main_middle',  label: '메인 중간'},
  {value: 'main_bottom',  label: '메인 하단'},
  {value: 'category_top', label: '카테고리 상단'},
  {value: 'product_top',  label: '상품상세 상단'},
  {value: 'event',        label: '이벤트 페이지'},
  {value: 'custom',       label: '직접 지정'}
];

const TYPES = [
  {value: 'single', label: '단일'},
  {value: 'slide',  label: '슬라이드'},
  {value: 'grid',   label: '그리드'}
];

const STATUS_FILTERS = [
  {value: '',          label: '전체'},
  {value: 'active',    label: '노출중'},
  {value: 'scheduled', label: '예약'},
  {value: 'expired',   label: '종료'},
  {value: 'inactive',  label: '비활성'}
];

const DEFAULT_BANNER = () => ({
  title: '',
  description: '',
  type: 'slide',
  position: 'main_top',
  customSelector: '',
  width:  {value: 100, unit: '%'},
  height: {value: 0,   unit: 'px'},
  items: [],
  startAt: null,
  endAt: null,
  isActive: true,
  slideConfig: {
    autoplay: true,
    autoplayDelay: 4000,
    loop: true,
    showNav: true,
    showPagination: true,
    paginationType: 'bullets',
    effect: 'slide',
    speed: 600,
    spaceBetween: 0,
    slidesPerView: 1
  },
  gridConfig: {columns: 3, rows: 1, gap: 8}
});

const DEFAULT_ITEM = () => ({
  imageUrl: '',
  mobileImageUrl: '',
  linkUrl: '',
  linkTarget: '_self',
  title: '',
  altText: '',
  order: 0
});

const blobToBase64 = (blob) => new Promise((resolve, reject) => {
  const reader = new FileReader();
  reader.onload = () => {
    const result = reader.result || '';
    const idx = String(result).indexOf(',');
    resolve(idx >= 0 ? String(result).slice(idx + 1) : String(result));
  };
  reader.onerror = (e) => reject(e);
  reader.readAsDataURL(blob);
});

@inject(({teamUser}) => ({isLogin: teamUser.isLogin, me: teamUser.me}))
@inject(({common}) => ({
  getBannerList: common.getBannerList,
  saveBanner: common.saveBanner,
  removeBanner: common.removeBanner,
  duplicateBanner: common.duplicateBanner,
  toggleBanner: common.toggleBanner,
  reorderBanners: common.reorderBanners,
  base64UploadImages: common.base64UploadImages,
  addMyImage: common.addMyImage,
  getMyImageList: common.getMyImageList
}))
@observer
class Banner extends Component {

  @observable store = {};
  @observable list = [];
  @observable totalCount = 0;
  @observable page = 1;
  @observable totalPages = 1;
  @observable limit = 30;

  @observable filterStatus = '';
  @observable filterPosition = '';
  @observable keyword = '';

  @observable isLoading = false;
  @observable isSaving = false;

  @observable editingBanner = null;
  @observable editTabIndex = 0;
  @observable showEditModal = false;
  @observable activeItemIdx = 0; // 라이브러리에서 선택한 이미지가 적용될 활성 슬라이드 idx

  @observable myImages = [];
  @observable myImagesLoading = false;
  @observable libKeyword = '';
  @observable libUploading = false;
  @observable libLoaded = false;

  @observable previewBanner = null;

  fileInputRef = React.createRef();
  searchTimer = null;

  componentDidMount() {
    if (this.props.me) {
      this.store = this.props.me.store ? this.props.me.store : {};
    }
    this.reload();
  }

  @computed get hasStore() { return !!(this.store && this.store._id); }

  @action reload = async () => {
    if (!this.hasStore) return;
    this.isLoading = true;
    try {
      const res = await this.props.getBannerList({
        store_id: this.store._id,
        shopNo: this.store.shopNo || 1,
        page: this.page,
        limit: this.limit,
        keyword: this.keyword || '',
        position: this.filterPosition || '',
        status: this.filterStatus || ''
      });
      if (res && res.data) {
        this.list = Array.isArray(res.data.docs) ? res.data.docs : [];
        this.totalCount = res.data.totalDocs || this.list.length;
        this.totalPages = res.data.totalPages || 1;
      }
    } catch (e) {
      toast.error('배너 목록 조회 실패');
    } finally {
      this.isLoading = false;
    }
  };

  @action onKeywordChange = (e) => {
    this.keyword = e.target.value;
    if (this.searchTimer) clearTimeout(this.searchTimer);
    this.searchTimer = setTimeout(() => { this.page = 1; this.reload(); }, 300);
  };

  @action setStatusFilter = (s) => { this.filterStatus = s; this.page = 1; this.reload(); };
  @action onPositionFilterChange = (e) => { this.filterPosition = e.target.value; this.page = 1; this.reload(); };
  @action goPage = (p) => { if (p < 1 || p > this.totalPages) return; this.page = p; this.reload(); };

  // === 모달 ===
  @action openCreate = () => {
    this.editingBanner = DEFAULT_BANNER();
    this.editTabIndex = 0;
    this.activeItemIdx = 0;
    this.libLoaded = false;
    this.showEditModal = true;
  };

  @action openEdit = (b) => {
    this.editingBanner = JSON.parse(JSON.stringify(b));
    const def = DEFAULT_BANNER();
    if (!this.editingBanner.slideConfig) this.editingBanner.slideConfig = def.slideConfig;
    if (!this.editingBanner.gridConfig)  this.editingBanner.gridConfig  = def.gridConfig;
    if (!this.editingBanner.width  || typeof this.editingBanner.width.value  !== 'number') this.editingBanner.width  = def.width;
    if (!this.editingBanner.height || typeof this.editingBanner.height.value !== 'number') this.editingBanner.height = def.height;
    if (!Array.isArray(this.editingBanner.items)) this.editingBanner.items = [];
    this.editTabIndex = 0;
    this.activeItemIdx = 0;
    this.libLoaded = false;
    this.showEditModal = true;
  };

  @action closeEdit = () => {
    this.showEditModal = false;
    this.editingBanner = null;
    this.activeItemIdx = 0;
  };

  @action setEditTab = (i) => {
    this.editTabIndex = i;
    if (i === 1 && !this.libLoaded) this.loadMyImages();
  };

  @action setActiveItem = (i) => {
    this.activeItemIdx = i;
  };

  @action setEditField = (key, value) => {
    if (!this.editingBanner) return;
    this.editingBanner[key] = value;
  };
  @action setSlideField = (key, value) => {
    this.editingBanner.slideConfig[key] = value;
  };
  @action setGridField = (key, value) => {
    this.editingBanner.gridConfig[key] = value;
  };
  @action setSizeField = (target, key, value) => {
    if (!this.editingBanner[target]) this.editingBanner[target] = {value: 0, unit: 'px'};
    this.editingBanner[target][key] = value;
  };

  @action addItem = () => {
    this.editingBanner.items.push(DEFAULT_ITEM());
    this.activeItemIdx = this.editingBanner.items.length - 1;
  };
  @action removeItem = (idx) => {
    this.editingBanner.items.splice(idx, 1);
    if (this.activeItemIdx >= this.editingBanner.items.length) {
      this.activeItemIdx = Math.max(0, this.editingBanner.items.length - 1);
    }
  };
  @action moveItem = (idx, dir) => {
    const next = idx + dir;
    if (next < 0 || next >= this.editingBanner.items.length) return;
    const items = this.editingBanner.items.slice();
    const tmp = items[idx]; items[idx] = items[next]; items[next] = tmp;
    this.editingBanner.items = items;
    if (this.activeItemIdx === idx) this.activeItemIdx = next;
    else if (this.activeItemIdx === next) this.activeItemIdx = idx;
  };
  @action setItemField = (idx, key, value) => {
    this.editingBanner.items[idx][key] = value;
  };
  @action clearItemImage = (idx) => {
    this.editingBanner.items[idx].imageUrl = '';
  };

  @action saveBanner = async () => {
    if (!this.editingBanner) return;
    if (!this.editingBanner.title || !this.editingBanner.title.trim()) {
      toast.error('제목을 입력하세요.');
      this.editTabIndex = 0;
      return;
    }
    if (this.editingBanner.type !== 'single' && this.editingBanner.items.length === 0) {
      toast.error('슬라이드를 최소 1개 이상 추가하세요.');
      this.editTabIndex = 1;
      return;
    }
    if (this.editingBanner.type === 'single' && this.editingBanner.items.length === 0) {
      this.editingBanner.items = [DEFAULT_ITEM()];
    }
    const missing = (this.editingBanner.items || []).find(it => !it.imageUrl);
    if (missing) { toast.error('이미지가 없는 슬라이드가 있습니다.'); this.editTabIndex = 1; return; }

    this.isSaving = true;
    try {
      const data = {...this.editingBanner};
      delete data._id;
      const res = await this.props.saveBanner({
        id: this.editingBanner._id || null,
        store_id: this.store._id,
        shopNo: this.store.shopNo || 1,
        data
      });
      if (res && res.data && res.data._id) {
        toast.success('배너가 저장되었습니다.');
        this.closeEdit();
        this.reload();
      } else {
        throw new Error('저장 실패');
      }
    } catch (e) {
      toast.error(e.message || '저장에 실패했습니다.');
    } finally {
      this.isSaving = false;
    }
  };

  // === 리스트 액션 ===
  toggleActive = async (b) => {
    try {
      await this.props.toggleBanner({id: b._id, store_id: this.store._id, isActive: !b.isActive});
      this.reload();
    } catch (e) { toast.error('상태 변경 실패'); }
  };

  duplicate = async (b) => {
    if (!window.confirm(`"${b.title}" 배너를 복제할까요?`)) return;
    try {
      const res = await this.props.duplicateBanner({id: b._id, store_id: this.store._id});
      if (res && res.data && res.data._id) { toast.success('복제 완료'); this.reload(); }
    } catch (e) { toast.error('복제 실패'); }
  };

  removeBanner = async (b) => {
    if (!window.confirm(`"${b.title}" 배너를 삭제할까요? 되돌릴 수 없습니다.`)) return;
    try {
      await this.props.removeBanner({id: b._id, store_id: this.store._id});
      toast.success('삭제 완료'); this.reload();
    } catch (e) { toast.error('삭제 실패'); }
  };

  // === 이미지 라이브러리 (인라인 패널) ===
  @action loadMyImages = async () => {
    if (!this.hasStore) return;
    this.myImagesLoading = true;
    try {
      const res = await this.props.getMyImageList({
        store_id: this.store._id,
        shopNo: this.store.shopNo || 1,
        page: 1,
        limit: 60,
        keyword: this.libKeyword || ''
      });
      this.myImages = (res && res.data && res.data.docs) ? res.data.docs : [];
      this.libLoaded = true;
    } catch (e) {} finally { this.myImagesLoading = false; }
  };
  @action onLibKeywordChange = (e) => {
    this.libKeyword = e.target.value;
    if (this.searchTimer) clearTimeout(this.searchTimer);
    this.searchTimer = setTimeout(() => this.loadMyImages(), 300);
  };
  @action applyImageToActive = (img) => {
    if (this.activeItemIdx === null || this.activeItemIdx === undefined) return;
    if (!this.editingBanner.items[this.activeItemIdx]) return;
    this.editingBanner.items[this.activeItemIdx].imageUrl = img.url;
    if (!this.editingBanner.items[this.activeItemIdx].altText) {
      this.editingBanner.items[this.activeItemIdx].altText = img.name || '';
    }
    // 다음 빈 슬라이드로 자동 이동 (연속 작업 가속)
    const items = this.editingBanner.items;
    for (let off = 1; off <= items.length; off++) {
      const next = (this.activeItemIdx + off) % items.length;
      if (!items[next].imageUrl) { this.activeItemIdx = next; break; }
    }
  };

  @action onLibFileChange = async (e) => {
    const files = Array.from(e.target.files || []);
    e.target.value = '';
    if (!files.length || !this.hasStore) return;
    this.libUploading = true;
    try {
      const base64Imgs = [];
      const dims = [];
      for (const f of files) {
        const b64 = await blobToBase64(f);
        const d = await this.readImageSize(f);
        base64Imgs.push({image: b64});
        dims.push(d);
      }
      const cafe24Res = await this.props.base64UploadImages({store_id: this.store._id, base64Imgs});
      if (cafe24Res && cafe24Res.error) throw new Error(cafe24Res.error.message || '업로드 실패');
      if (!cafe24Res || !cafe24Res.images) throw new Error('업로드 실패');

      let firstUrl = null;
      let firstName = '';
      for (let i = 0; i < cafe24Res.images.length; i++) {
        const url = cafe24Res.images[i].path;
        const file = files[i] || files[0];
        if (!firstUrl) { firstUrl = url; firstName = file.name || ''; }
        const dim = dims[i] || {width: 0, height: 0};
        try {
          await this.props.addMyImage({
            store_id: this.store._id,
            shopNo: this.store.shopNo || 1,
            name: file.name || '이미지',
            url,
            size: file.size || 0,
            width: dim.width,
            height: dim.height,
            source: 'banner'
          });
        } catch (e) {}
      }
      toast.success(`${cafe24Res.images.length}개 업로드`);
      // 업로드한 첫 이미지를 활성 슬라이드에 즉시 적용
      if (firstUrl && this.editingBanner && this.editingBanner.items[this.activeItemIdx]) {
        this.applyImageToActive({url: firstUrl, name: firstName});
      }
      this.loadMyImages();
    } catch (e) {
      toast.error(e.message || '업로드 실패');
    } finally {
      this.libUploading = false;
    }
  };

  readImageSize = (file) => new Promise((resolve) => {
    const url = URL.createObjectURL(file);
    const img = new Image();
    img.onload = () => { resolve({width: img.naturalWidth, height: img.naturalHeight}); URL.revokeObjectURL(url); };
    img.onerror = () => { resolve({width: 0, height: 0}); URL.revokeObjectURL(url); };
    img.src = url;
  });

  // === 미리보기 ===
  @action openPreview = (b) => { this.previewBanner = b; };
  @action closePreview = () => { this.previewBanner = null; };

  // === 헬퍼 ===
  positionLabel = (val) => {
    const p = POSITIONS.find(x => x.value === val);
    return p ? p.label : val;
  };
  typeLabel = (val) => {
    const t = TYPES.find(x => x.value === val);
    return t ? t.label : val;
  };
  bannerStatus = (b) => {
    const now = new Date();
    if (!b.isActive) return {label: '비활성', color: '#9CA3AF', bg: '#F3F4F6'};
    if (b.startAt && new Date(b.startAt) > now) return {label: '예약', color: '#0891B2', bg: '#CFFAFE'};
    if (b.endAt && new Date(b.endAt) < now) return {label: '종료', color: '#B91C1C', bg: '#FEE2E2'};
    return {label: '노출중', color: '#065F46', bg: '#D1FAE5'};
  };
  formatPeriod = (b) => {
    const fmt = 'YY.MM.DD';
    if (!b.startAt && !b.endAt) return '상시 노출';
    const s = b.startAt ? moment(b.startAt).format(fmt) : '∞';
    const e = b.endAt ? moment(b.endAt).format(fmt) : '∞';
    return `${s} ~ ${e}`;
  };

  // === 렌더 ===
  render() {
    return (
      <div className="main-content">
        <PageTitle/>
        <div className="widget-box banner-page">

          <div className="bnr-header">
            <div className="bnr-header__title">
              <p>쇼핑몰 화면에 노출되는 배너(단일/슬라이드/그리드)를 등록·편집·관리합니다.</p>
            </div>
            <div className="bnr-header__cta">
              <Button theme="primary" onClick={this.openCreate}>
                <FeatherIcon icon="plus" size="14"/><span> 배너 추가</span>
              </Button>
            </div>
          </div>

          <div className="bnr-toolbar">
            <div className="bnr-toolbar__filters">
              <div className="bnr-search">
                <FeatherIcon icon="search" size="14"/>
                <FormInput placeholder="배너 제목/설명 검색" value={this.keyword} onChange={this.onKeywordChange}/>
              </div>
              <FormSelect value={this.filterPosition} onChange={this.onPositionFilterChange} className="bnr-select">
                <option value="">모든 위치</option>
                {POSITIONS.map(p => <option key={p.value} value={p.value}>{p.label}</option>)}
              </FormSelect>
            </div>
            <div className="bnr-toolbar__chips">
              {STATUS_FILTERS.map(s => (
                <button key={s.value || 'all'}
                        className={`bnr-chip ${this.filterStatus === s.value ? 'is-on' : ''}`}
                        onClick={() => this.setStatusFilter(s.value)}>
                  {s.label}
                </button>
              ))}
            </div>
            <div className="bnr-toolbar__meta">
              전체 <b>{this.totalCount}</b>개
            </div>
          </div>

          {this.isLoading ? (
            <div className="bnr-state"><FeatherIcon icon="loader" size="36"/><div>불러오는 중…</div></div>
          ) : this.list.length === 0 ? (
            <div className="bnr-state">
              <FeatherIcon icon="image" size="48"/>
              <div className="bnr-state__text">{this.keyword || this.filterPosition || this.filterStatus ? '조건에 맞는 배너가 없습니다.' : '등록된 배너가 없습니다.'}</div>
              {!(this.keyword || this.filterPosition || this.filterStatus) && (
                <Button theme="primary" size="sm" onClick={this.openCreate}>
                  <FeatherIcon icon="plus" size="14"/><span> 첫 배너 만들기</span>
                </Button>
              )}
            </div>
          ) : (
            <div className="bnr-grid">
              {this.list.map((b) => {
                const status = this.bannerStatus(b);
                const cover = (b.items && b.items[0] && b.items[0].imageUrl) || '';
                const itemCount = b.items ? b.items.length : 0;
                return (
                  <div key={b._id}
                       className={`bnr-card ${!b.isActive ? 'is-off' : ''}`}
                       onClick={() => this.openPreview(b)}
                       title="클릭하여 미리보기">
                    <div className="bnr-card__cover">
                      {cover
                        ? <img src={cover} alt={b.title}/>
                        : <div className="bnr-card__cover-empty"><FeatherIcon icon="image" size="32"/></div>
                      }
                      <span className="bnr-card__status" style={{color: status.color, background: status.bg}}>
                        {status.label}
                      </span>
                      {itemCount > 1 && (
                        <span className="bnr-card__count">
                          <FeatherIcon icon="layers" size="11"/> {itemCount}
                        </span>
                      )}
                      <span className="bnr-card__playhint">
                        <FeatherIcon icon="play" size="14"/>
                      </span>
                    </div>
                    <div className="bnr-card__body">
                      <div className="bnr-card__title" title={b.title}>{b.title}</div>
                      <div className="bnr-card__meta">
                        <span className="bnr-tag">{this.typeLabel(b.type)}</span>
                        <span className="bnr-tag bnr-tag--pos">{this.positionLabel(b.position)}</span>
                      </div>
                      <div className="bnr-card__period">
                        <FeatherIcon icon="calendar" size="11"/> {this.formatPeriod(b)}
                      </div>
                      <div className="bnr-card__actions" onClick={(e) => e.stopPropagation()}>
                        <button className="bnr-iconbtn" onClick={() => this.toggleActive(b)} title={b.isActive ? '비활성화' : '활성화'}>
                          <FeatherIcon icon={b.isActive ? 'toggle-right' : 'toggle-left'} size="16"/>
                        </button>
                        <button className="bnr-iconbtn" onClick={() => this.openEdit(b)} title="편집">
                          <FeatherIcon icon="edit-2" size="14"/>
                        </button>
                        <button className="bnr-iconbtn" onClick={() => this.duplicate(b)} title="복제">
                          <FeatherIcon icon="copy" size="14"/>
                        </button>
                        <button className="bnr-iconbtn bnr-iconbtn--danger" onClick={() => this.removeBanner(b)} title="삭제">
                          <FeatherIcon icon="trash-2" size="14"/>
                        </button>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          )}

          {this.totalPages > 1 && (
            <div className="bnr-pager">
              <Button size="sm" theme="light" disabled={this.page <= 1} onClick={() => this.goPage(this.page - 1)}>
                <FeatherIcon icon="chevron-left" size="14"/>
              </Button>
              <span>{this.page} / {this.totalPages}</span>
              <Button size="sm" theme="light" disabled={this.page >= this.totalPages} onClick={() => this.goPage(this.page + 1)}>
                <FeatherIcon icon="chevron-right" size="14"/>
              </Button>
            </div>
          )}
        </div>

        {/* === 편집 모달 === */}
        {this.showEditModal && this.editingBanner && (
          <Modal open={this.showEditModal} toggle={() => {}} size="lg"
                 className={`bnr-modal ${this.editTabIndex === 1 ? 'bnr-modal--wide' : ''}`}>
            <ModalHeader toggle={this.closeEdit}>
              {this.editingBanner._id ? '배너 편집' : '배너 추가'}
            </ModalHeader>
            <ModalBody>
              <div className="bnr-tabs">
                {['기본 정보', '슬라이드 / 이미지', '노출 설정', '동작 설정'].map((label, i) => (
                  <button key={i}
                          className={`bnr-tab ${this.editTabIndex === i ? 'is-on' : ''}`}
                          onClick={() => this.setEditTab(i)}>
                    {label}
                  </button>
                ))}
              </div>

              {this.editTabIndex === 0 && this.renderTabBasic()}
              {this.editTabIndex === 1 && this.renderTabItems()}
              {this.editTabIndex === 2 && this.renderTabSchedule()}
              {this.editTabIndex === 3 && this.renderTabBehavior()}
            </ModalBody>
            <ModalFooter>
              <Button theme="light" onClick={this.closeEdit}>취소</Button>
              <Button theme="primary" disabled={this.isSaving} onClick={this.saveBanner}>
                <FeatherIcon icon={this.isSaving ? 'loader' : 'save'} size="14"/>
                <span> {this.isSaving ? '저장 중…' : '저장'}</span>
              </Button>
            </ModalFooter>
          </Modal>
        )}

        {/* === 미리보기 === */}
        {this.previewBanner && this.renderPreview()}
      </div>
    );
  }

  renderPreview = () => {
    const b = this.previewBanner;
    const items = (b.items || []).filter(it => it.imageUrl);
    const sizeStyle = this.bannerSizeStyle(b);
    const fixedHeight = b.height && b.height.value > 0;
    return (
      <div className="bnr-preview" onClick={this.closePreview}>
        <div className="bnr-preview__inner" onClick={(e) => e.stopPropagation()}>
          <div className="bnr-preview__head">
            <div>
              <div className="bnr-preview__title">{b.title}</div>
              <div className="bnr-preview__meta">
                {this.typeLabel(b.type)} · {this.positionLabel(b.position)} · {items.length}장
                {b.width && b.height && (
                  <> · {b.width.value}{b.width.unit} × {fixedHeight ? `${b.height.value}${b.height.unit}` : 'auto'}</>
                )}
              </div>
            </div>
            <button className="bnr-preview__close" onClick={this.closePreview} title="닫기">
              <FeatherIcon icon="x" size="20"/>
            </button>
          </div>
          <div className="bnr-preview__stage">
            {items.length === 0 ? (
              <div className="bnr-preview__empty">
                <FeatherIcon icon="image" size="48"/>
                <div>표시할 이미지가 없습니다.</div>
              </div>
            ) : (
              <div className={`bnr-preview__frame ${fixedHeight ? 'is-fixed' : ''}`} style={sizeStyle}>
                {b.type === 'slide'
                  ? this.renderPreviewSlide(b, items, fixedHeight)
                  : b.type === 'grid'
                  ? this.renderPreviewGrid(b, items)
                  : this.renderPreviewSingle(items, fixedHeight)}
              </div>
            )}
          </div>
        </div>
      </div>
    );
  };

  bannerSizeStyle = (b) => {
    const style = {};
    if (b.width && b.width.value > 0) {
      style.width = `${b.width.value}${b.width.unit || '%'}`;
    } else {
      style.width = '100%';
    }
    if (b.height && b.height.value > 0) {
      style.height = `${b.height.value}${b.height.unit || 'px'}`;
    }
    style.maxWidth = '100%';
    return style;
  };

  renderPreviewSlide = (b, items, fixedHeight) => {
    const s = b.slideConfig || {};
    return (
      <Swiper
        key={`${b._id}-${items.length}-${s.effect}-${s.autoplay}-${s.autoplayDelay}-${s.loop}-${s.speed}-${s.spaceBetween}-${s.slidesPerView}-${s.showNav}-${s.showPagination}-${s.paginationType}-${fixedHeight}`}
        className={`bnr-preview__swiper ${fixedHeight ? 'is-fixed' : ''}`}
        effect={s.effect === 'fade' ? 'fade' : 'slide'}
        speed={s.speed || 600}
        spaceBetween={s.spaceBetween || 0}
        slidesPerView={s.slidesPerView || 1}
        loop={!!s.loop && items.length > (s.slidesPerView || 1)}
        autoplay={s.autoplay ? {delay: s.autoplayDelay || 4000, disableOnInteraction: false} : false}
        navigation={!!s.showNav}
        pagination={s.showPagination ? {type: s.paginationType || 'bullets', clickable: true} : false}
      >
        {items.map((it, i) => (
          <SwiperSlide key={i}>
            {it.linkUrl ? (
              <a href={it.linkUrl} target={it.linkTarget || '_self'} rel="noopener noreferrer">
                <img src={it.imageUrl} alt={it.altText || ''}/>
              </a>
            ) : (
              <img src={it.imageUrl} alt={it.altText || ''}/>
            )}
          </SwiperSlide>
        ))}
      </Swiper>
    );
  };

  renderPreviewGrid = (b, items) => {
    const g = b.gridConfig || {};
    return (
      <div className="bnr-preview__grid" style={{
        display: 'grid',
        gridTemplateColumns: `repeat(${g.columns || 3}, 1fr)`,
        gap: `${g.gap || 8}px`,
        height: '100%'
      }}>
        {items.map((it, i) => (
          <div key={i} className="bnr-preview__cell">
            {it.linkUrl ? (
              <a href={it.linkUrl} target={it.linkTarget || '_self'} rel="noopener noreferrer">
                <img src={it.imageUrl} alt={it.altText || ''}/>
              </a>
            ) : (
              <img src={it.imageUrl} alt={it.altText || ''}/>
            )}
          </div>
        ))}
      </div>
    );
  };

  renderPreviewSingle = (items, fixedHeight) => {
    const it = items[0];
    return (
      <div className={`bnr-preview__single ${fixedHeight ? 'is-fixed' : ''}`}>
        {it.linkUrl ? (
          <a href={it.linkUrl} target={it.linkTarget || '_self'} rel="noopener noreferrer">
            <img src={it.imageUrl} alt={it.altText || ''}/>
          </a>
        ) : (
          <img src={it.imageUrl} alt={it.altText || ''}/>
        )}
      </div>
    );
  };

  // === 탭 렌더 함수 ===
  renderTabBasic() {
    const b = this.editingBanner;
    return (
      <div className="bnr-tabpanel">
        <div className="bnr-field">
          <label>제목 <span className="bnr-req">*</span></label>
          <FormInput value={b.title || ''} onChange={(e) => this.setEditField('title', e.target.value)}
                     placeholder="예) 봄 시즌 메인 배너"/>
        </div>
        <div className="bnr-field">
          <label>설명 (메모)</label>
          <FormTextarea rows="2" value={b.description || ''}
                        onChange={(e) => this.setEditField('description', e.target.value)}
                        placeholder="관리용 메모입니다. 노출되지 않습니다."/>
        </div>
        <div className="bnr-row">
          <div className="bnr-field bnr-field--half">
            <label>배너 타입</label>
            <div className="bnr-typegrid">
              {TYPES.map(t => (
                <button key={t.value}
                        className={`bnr-typecard ${b.type === t.value ? 'is-on' : ''}`}
                        onClick={() => this.setEditField('type', t.value)}>
                  <FeatherIcon icon={t.value === 'single' ? 'image' : t.value === 'slide' ? 'sliders' : 'grid'} size="18"/>
                  <span>{t.label}</span>
                </button>
              ))}
            </div>
          </div>
          <div className="bnr-field bnr-field--half">
            <label>노출 위치</label>
            <FormSelect value={b.position} onChange={(e) => this.setEditField('position', e.target.value)}>
              {POSITIONS.map(p => <option key={p.value} value={p.value}>{p.label}</option>)}
            </FormSelect>
            {b.position === 'custom' && (
              <FormInput style={{marginTop: 8}} placeholder="CSS 셀렉터 (예: .main-banner-area)"
                         value={b.customSelector || ''}
                         onChange={(e) => this.setEditField('customSelector', e.target.value)}/>
            )}
          </div>
        </div>
        {b.type === 'grid' && (
          <div className="bnr-row">
            <div className="bnr-field bnr-field--third">
              <label>열 수</label>
              <FormInput type="number" min="1" max="6" value={b.gridConfig.columns}
                         onChange={(e) => this.setGridField('columns', parseInt(e.target.value, 10) || 1)}/>
            </div>
            <div className="bnr-field bnr-field--third">
              <label>행 수</label>
              <FormInput type="number" min="1" max="6" value={b.gridConfig.rows}
                         onChange={(e) => this.setGridField('rows', parseInt(e.target.value, 10) || 1)}/>
            </div>
            <div className="bnr-field bnr-field--third">
              <label>간격(px)</label>
              <FormInput type="number" min="0" value={b.gridConfig.gap}
                         onChange={(e) => this.setGridField('gap', parseInt(e.target.value, 10) || 0)}/>
            </div>
          </div>
        )}

        <div className="bnr-row">
          <div className="bnr-field bnr-field--half">
            <label>너비</label>
            <div className="bnr-sizeinput">
              <FormInput type="number" min="0" value={b.width.value}
                         onChange={(e) => this.setSizeField('width', 'value', parseInt(e.target.value, 10) || 0)}/>
              <FormSelect value={b.width.unit}
                          onChange={(e) => this.setSizeField('width', 'unit', e.target.value)}>
                <option value="%">%</option>
                <option value="px">px</option>
              </FormSelect>
            </div>
            <div className="bnr-help">기본 100% — 영역에 꽉 채워 표시됩니다.</div>
          </div>
          <div className="bnr-field bnr-field--half">
            <label>높이</label>
            <div className="bnr-sizeinput">
              <FormInput type="number" min="0" value={b.height.value}
                         onChange={(e) => this.setSizeField('height', 'value', parseInt(e.target.value, 10) || 0)}/>
              <FormSelect value={b.height.unit}
                          onChange={(e) => this.setSizeField('height', 'unit', e.target.value)}>
                <option value="px">px</option>
                <option value="%">%</option>
              </FormSelect>
            </div>
            <div className="bnr-help">0 입력시 자동(이미지 비율 유지). 값이 있으면 이미지가 cover로 잘립니다.</div>
          </div>
        </div>
      </div>
    );
  }

  renderTabItems() {
    const b = this.editingBanner;
    const isMulti = b.type !== 'single';
    return (
      <div className="bnr-tabpanel bnr-itemtab">
        {/* 좌: 슬라이드 카드 리스트 */}
        <div className="bnr-itemtab__left">
          <div className="bnr-itemshead">
            <div className="bnr-itemshead__label">
              {isMulti ? '슬라이드' : '단일 이미지'}
              <span className="bnr-itemshead__count"> · {b.items.length}개</span>
            </div>
            {(isMulti || b.items.length === 0) && (
              <Button theme="primary" size="sm" onClick={this.addItem}>
                <FeatherIcon icon="plus" size="14"/><span> 추가</span>
              </Button>
            )}
          </div>
          {b.items.length === 0 ? (
            <div className="bnr-state bnr-state--small">
              <FeatherIcon icon="image" size="32"/>
              <div>슬라이드를 추가한 뒤 우측에서 이미지를 선택하세요.</div>
              <Button theme="primary" size="sm" onClick={this.addItem}>
                <FeatherIcon icon="plus" size="14"/><span> 슬라이드 추가</span>
              </Button>
            </div>
          ) : (
            <div className="bnr-itemlist">
              {b.items.map((it, i) => {
                const isActive = i === this.activeItemIdx;
                return (
                  <div key={i}
                       className={`bnr-item ${isActive ? 'is-active' : ''} ${!it.imageUrl ? 'is-empty' : ''}`}
                       onClick={() => this.setActiveItem(i)}>
                    <div className="bnr-item__head">
                      <span className="bnr-item__order">{i + 1}</span>
                      <span className="bnr-item__title">
                        {it.altText || it.linkUrl || `슬라이드 ${i + 1}`}
                      </span>
                      <div className="bnr-item__headactions" onClick={(e) => e.stopPropagation()}>
                        <button className="bnr-iconbtn" onClick={() => this.moveItem(i, -1)} disabled={i === 0} title="위로">
                          <FeatherIcon icon="arrow-up" size="13"/>
                        </button>
                        <button className="bnr-iconbtn" onClick={() => this.moveItem(i, 1)} disabled={i === b.items.length - 1} title="아래로">
                          <FeatherIcon icon="arrow-down" size="13"/>
                        </button>
                        <button className="bnr-iconbtn bnr-iconbtn--danger" onClick={() => this.removeItem(i)} title="삭제">
                          <FeatherIcon icon="trash-2" size="13"/>
                        </button>
                      </div>
                    </div>
                    <div className="bnr-item__main">
                      <div className="bnr-item__thumb">
                        {it.imageUrl ? (
                          <>
                            <img src={it.imageUrl} alt=""/>
                            <button className="bnr-item__clear" onClick={(e) => { e.stopPropagation(); this.clearItemImage(i); }} title="이미지 제거">
                              <FeatherIcon icon="x" size="12"/>
                            </button>
                          </>
                        ) : (
                          <div className="bnr-item__placeholder">
                            <FeatherIcon icon="image" size="22"/>
                            <span>{isActive ? '우측에서 이미지 선택' : '클릭 후 이미지 선택'}</span>
                          </div>
                        )}
                      </div>
                      <div className="bnr-item__fields" onClick={(e) => e.stopPropagation()}>
                        <div className="bnr-field">
                          <label>링크 URL</label>
                          <FormInput placeholder="https://example.com/event/spring" value={it.linkUrl}
                                     onChange={(e) => this.setItemField(i, 'linkUrl', e.target.value)}/>
                        </div>
                        <div className="bnr-row">
                          <div className="bnr-field bnr-field--half">
                            <label>대체 텍스트</label>
                            <FormInput value={it.altText} onChange={(e) => this.setItemField(i, 'altText', e.target.value)}
                                       placeholder="이미지 설명"/>
                          </div>
                          <div className="bnr-field bnr-field--half">
                            <label>&nbsp;</label>
                            <FormCheckbox checked={it.linkTarget === '_blank'}
                                          onChange={(e) => this.setItemField(i, 'linkTarget', e.target.checked ? '_blank' : '_self')}>
                              새 탭에서 열기
                            </FormCheckbox>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>

        {/* 우: 이미지 라이브러리 (스티키) */}
        <div className="bnr-itemtab__right">
          <div className="bnr-lib">
            <div className="bnr-lib__head">
              <div className="bnr-lib__title">
                <FeatherIcon icon="image" size="14"/>
                <span>이미지 라이브러리</span>
                {b.items.length > 0 && (
                  <span className="bnr-lib__active">→ 슬라이드 {this.activeItemIdx + 1}에 적용</span>
                )}
              </div>
            </div>
            <div className="bnr-lib__toolbar">
              <div className="bnr-search">
                <FeatherIcon icon="search" size="14"/>
                <FormInput placeholder="이름 검색" value={this.libKeyword} onChange={this.onLibKeywordChange}/>
              </div>
              <input type="file" multiple accept="image/*" ref={this.fileInputRef}
                     style={{display: 'none'}} onChange={this.onLibFileChange}/>
              <Button theme="primary" size="sm" disabled={this.libUploading}
                      onClick={() => this.fileInputRef.current && this.fileInputRef.current.click()}>
                <FeatherIcon icon={this.libUploading ? 'loader' : 'upload'} size="14"/>
                <span> {this.libUploading ? '업로드…' : '업로드'}</span>
              </Button>
            </div>
            <div className="bnr-lib__body">
              {this.myImagesLoading ? (
                <div className="bnr-lib__loading"><FeatherIcon icon="loader" size="28"/></div>
              ) : this.myImages.length === 0 ? (
                <div className="bnr-lib__empty">
                  <FeatherIcon icon="image" size="36"/>
                  <div>등록된 이미지가 없습니다.</div>
                  <div className="bnr-lib__emptyhint">우측 상단 <b>업로드</b> 버튼으로 추가하거나, 디자인 관리 &gt; 내 이미지에서 등록할 수 있습니다.</div>
                </div>
              ) : (
                <div className="bnr-lib__grid">
                  {this.myImages.map(img => {
                    const isCurrent = b.items[this.activeItemIdx] && b.items[this.activeItemIdx].imageUrl === img.url;
                    return (
                      <button key={img._id}
                              className={`bnr-lib__item ${isCurrent ? 'is-current' : ''}`}
                              onClick={() => this.applyImageToActive(img)}
                              disabled={b.items.length === 0}
                              title={img.name}>
                        <img src={img.url} alt={img.name}/>
                        {isCurrent && (
                          <span className="bnr-lib__check">
                            <FeatherIcon icon="check" size="14"/>
                          </span>
                        )}
                      </button>
                    );
                  })}
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    );
  }

  renderTabSchedule() {
    const b = this.editingBanner;
    const toLocal = (d) => d ? moment(d).format('YYYY-MM-DDTHH:mm') : '';
    const toDate = (s) => s ? new Date(s) : null;
    return (
      <div className="bnr-tabpanel">
        <div className="bnr-field">
          <label>활성 상태</label>
          <FormCheckbox toggle checked={!!b.isActive} onChange={(e) => this.setEditField('isActive', e.target.checked)}>
            이 배너를 사용합니다 (꺼두면 일정과 무관하게 노출되지 않음)
          </FormCheckbox>
        </div>
        <div className="bnr-row">
          <div className="bnr-field bnr-field--half">
            <label>시작일시</label>
            <FormInput type="datetime-local" value={toLocal(b.startAt)}
                       onChange={(e) => this.setEditField('startAt', toDate(e.target.value))}/>
            <div className="bnr-help">비워두면 즉시 노출 시작</div>
          </div>
          <div className="bnr-field bnr-field--half">
            <label>종료일시</label>
            <FormInput type="datetime-local" value={toLocal(b.endAt)}
                       onChange={(e) => this.setEditField('endAt', toDate(e.target.value))}/>
            <div className="bnr-help">비워두면 무기한 노출</div>
          </div>
        </div>
      </div>
    );
  }

  renderTabBehavior() {
    const b = this.editingBanner;
    if (b.type !== 'slide') {
      return (
        <div className="bnr-tabpanel">
          <div className="bnr-state bnr-state--small">
            <FeatherIcon icon="info" size="22"/>
            <div>슬라이드 타입에서만 동작 설정이 적용됩니다.</div>
          </div>
        </div>
      );
    }
    const s = b.slideConfig;
    return (
      <div className="bnr-tabpanel">
        <div className="bnr-row">
          <div className="bnr-field bnr-field--half">
            <label>전환 효과</label>
            <FormSelect value={s.effect} onChange={(e) => this.setSlideField('effect', e.target.value)}>
              <option value="slide">slide (슬라이드)</option>
              <option value="fade">fade (페이드)</option>
            </FormSelect>
          </div>
          <div className="bnr-field bnr-field--half">
            <label>한번에 보이는 슬라이드</label>
            <FormInput type="number" min="1" max="6" value={s.slidesPerView}
                       onChange={(e) => this.setSlideField('slidesPerView', parseInt(e.target.value, 10) || 1)}/>
          </div>
        </div>
        <div className="bnr-row">
          <div className="bnr-field bnr-field--half">
            <label>전환 속도(ms)</label>
            <FormInput type="number" min="100" step="100" value={s.speed}
                       onChange={(e) => this.setSlideField('speed', parseInt(e.target.value, 10) || 600)}/>
          </div>
          <div className="bnr-field bnr-field--half">
            <label>슬라이드 간격(px)</label>
            <FormInput type="number" min="0" value={s.spaceBetween}
                       onChange={(e) => this.setSlideField('spaceBetween', parseInt(e.target.value, 10) || 0)}/>
          </div>
        </div>
        <div className="bnr-field">
          <label>자동 재생</label>
          <FormCheckbox toggle checked={!!s.autoplay} onChange={(e) => this.setSlideField('autoplay', e.target.checked)}>
            자동 슬라이드 사용
          </FormCheckbox>
          {s.autoplay && (
            <div style={{marginTop: 8, display: 'flex', gap: 8, alignItems: 'center'}}>
              <span style={{fontSize: 12, color: '#6B7280'}}>전환 간격:</span>
              <FormInput type="number" min="500" step="500" style={{width: 120}}
                         value={s.autoplayDelay}
                         onChange={(e) => this.setSlideField('autoplayDelay', parseInt(e.target.value, 10) || 4000)}/>
              <span style={{fontSize: 12, color: '#6B7280'}}>ms</span>
            </div>
          )}
        </div>
        <div className="bnr-field">
          <label>옵션</label>
          <div className="bnr-checkrow">
            <FormCheckbox checked={!!s.loop} onChange={(e) => this.setSlideField('loop', e.target.checked)}>
              무한 루프
            </FormCheckbox>
            <FormCheckbox checked={!!s.showNav} onChange={(e) => this.setSlideField('showNav', e.target.checked)}>
              좌/우 네비게이션 버튼
            </FormCheckbox>
            <FormCheckbox checked={!!s.showPagination} onChange={(e) => this.setSlideField('showPagination', e.target.checked)}>
              페이지네이션 표시
            </FormCheckbox>
          </div>
        </div>
        {s.showPagination && (
          <div className="bnr-field">
            <label>페이지네이션 종류</label>
            <FormSelect value={s.paginationType} onChange={(e) => this.setSlideField('paginationType', e.target.value)}>
              <option value="bullets">bullets (점 표시)</option>
              <option value="progressbar">progressbar (진행 바)</option>
              <option value="fraction">fraction (1/N 형식)</option>
            </FormSelect>
          </div>
        )}
      </div>
    );
  }
}

export default Banner;
